import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Droplets, Check, Pencil, X, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, addDays, isToday, isBefore, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface CareCalendarProps {
  plantId: string;
}

interface CareSchedule {
  id: string;
  frequency_days: number;
  next_care_date: string;
  care_type: string;
}

interface CareLog {
  id: string;
  completed_at: string;
  care_type: string;
}

export function CareCalendar({ plantId }: CareCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [schedule, setSchedule] = useState<CareSchedule | null>(null);
  const [careLogs, setCareLogs] = useState<CareLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editFrequency, setEditFrequency] = useState('');
  const [saving, setSaving] = useState(false);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editDate, setEditDate] = useState<Date>(new Date());

  useEffect(() => {
    fetchData();
  }, [plantId]);

  const fetchData = async () => {
    try {
      // Fetch care schedule
      const { data: scheduleData } = await supabase
        .from('plant_care_schedules')
        .select('*')
        .eq('plant_id', plantId)
        .eq('care_type', 'watering')
        .maybeSingle();

      setSchedule(scheduleData);

      // Fetch care logs
      const { data: logsData } = await supabase
        .from('care_logs')
        .select('*')
        .eq('plant_id', plantId)
        .eq('care_type', 'watering')
        .order('completed_at', { ascending: false });

      setCareLogs(logsData || []);
    } catch (error) {
      console.error('Error fetching care data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (schedule) {
      setEditFrequency(String(schedule.frequency_days));
      setIsEditing(true);
    }
  };

  const handleSaveFrequency = async () => {
    if (!schedule) return;

    const newFrequency = parseInt(editFrequency);
    if (isNaN(newFrequency) || newFrequency < 1 || newFrequency > 90) {
      toast.error('A frequência deve ser entre 1 e 90 dias');
      return;
    }

    setSaving(true);
    try {
      // Get the last completed care log to calculate new next_care_date
      const lastLog = careLogs[0];
      const baseDate = lastLog ? new Date(lastLog.completed_at) : new Date();
      const newNextCareDate = addDays(startOfDay(baseDate), newFrequency);

      const { error } = await supabase
        .from('plant_care_schedules')
        .update({
          frequency_days: newFrequency,
          next_care_date: format(newNextCareDate, 'yyyy-MM-dd'),
          updated_at: new Date().toISOString()
        })
        .eq('id', schedule.id);

      if (error) throw error;

      setSchedule({
        ...schedule,
        frequency_days: newFrequency,
        next_care_date: format(newNextCareDate, 'yyyy-MM-dd')
      });
      setIsEditing(false);
      toast.success('Frequência atualizada!');
    } catch (error) {
      console.error('Error updating frequency:', error);
      toast.error('Erro ao atualizar frequência');
    } finally {
      setSaving(false);
    }
  };

  const getLastWateringDate = (): Date => {
    if (careLogs.length > 0) {
      return new Date(careLogs[0].completed_at);
    }
    // If no logs, calculate backwards from next_care_date
    if (schedule) {
      return addDays(new Date(schedule.next_care_date), -schedule.frequency_days);
    }
    return new Date();
  };

  const handleStartEditDate = () => {
    setEditDate(getLastWateringDate());
    setIsEditingDate(true);
  };

  const handleSaveLastWateringDate = async () => {
    if (!schedule) return;

    setSaving(true);
    try {
      const newNextCareDate = addDays(startOfDay(editDate), schedule.frequency_days);

      const { error } = await supabase
        .from('plant_care_schedules')
        .update({
          next_care_date: format(newNextCareDate, 'yyyy-MM-dd'),
          updated_at: new Date().toISOString()
        })
        .eq('id', schedule.id);

      if (error) throw error;

      setSchedule({
        ...schedule,
        next_care_date: format(newNextCareDate, 'yyyy-MM-dd')
      });
      setIsEditingDate(false);
      toast.success('Data da última rega atualizada!');
    } catch (error) {
      console.error('Error updating last watering date:', error);
      toast.error('Erro ao atualizar data');
    } finally {
      setSaving(false);
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get first day of week offset
  const startDay = monthStart.getDay();

  // Calculate scheduled days for the month
  const getScheduledDays = (): Date[] => {
    if (!schedule) return [];
    
    const scheduledDays: Date[] = [];
    let nextDate = new Date(schedule.next_care_date);
    
    // Go back to find any scheduled dates before next_care_date within the month
    while (nextDate > monthStart) {
      nextDate = addDays(nextDate, -schedule.frequency_days);
    }
    
    // Move forward to first date within or after month start
    while (nextDate < monthStart) {
      nextDate = addDays(nextDate, schedule.frequency_days);
    }
    
    // Add all scheduled dates within the month
    while (nextDate <= monthEnd) {
      scheduledDays.push(new Date(nextDate));
      nextDate = addDays(nextDate, schedule.frequency_days);
    }
    
    return scheduledDays;
  };

  const scheduledDays = getScheduledDays();

  const isScheduledDay = (date: Date): boolean => {
    return scheduledDays.some(d => isSameDay(d, date));
  };

  const isCompletedDay = (date: Date): boolean => {
    return careLogs.some(log => {
      const logDate = new Date(log.completed_at);
      return isSameDay(logDate, date);
    });
  };

  const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-6 bg-muted rounded w-1/2 mx-auto" />
            <div className="grid grid-cols-7 gap-1">
              {[...Array(35)].map((_, i) => (
                <div key={i} className="h-8 bg-muted rounded" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!schedule) {
    return (
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Droplets className="h-5 w-5 text-water" />
            Calendário de Cuidados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhum cronograma de rega configurado
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Droplets className="h-5 w-5 text-water" />
          Calendário de Cuidados
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium text-sm capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="h-8 w-8"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Week days header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day, i) => (
            <div key={i} className="text-center text-xs text-muted-foreground font-medium">
              {day}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for offset */}
          {[...Array(startDay)].map((_, i) => (
            <div key={`empty-${i}`} className="h-8" />
          ))}
          
          {/* Day cells */}
          {days.map((day) => {
            const completed = isCompletedDay(day);
            const scheduled = isScheduledDay(day);
            const today = isToday(day);
            const isPast = isBefore(startOfDay(day), startOfDay(new Date()));

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "h-8 flex items-center justify-center rounded-md text-sm relative",
                  today && "ring-2 ring-primary ring-offset-1 ring-offset-background",
                  completed && "bg-primary/20 text-primary",
                  scheduled && !completed && !isPast && "bg-water/20 text-water",
                  !completed && !scheduled && "text-muted-foreground"
                )}
              >
                {format(day, 'd')}
                {completed && (
                  <Check className="h-3 w-3 absolute -top-0.5 -right-0.5 text-primary" />
                )}
                {scheduled && !completed && !isPast && (
                  <Droplets className="h-3 w-3 absolute -top-0.5 -right-0.5 text-water" />
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-primary/20 flex items-center justify-center">
              <Check className="h-2 w-2 text-primary" />
            </div>
            <span>Realizada</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-water/20 flex items-center justify-center">
              <Droplets className="h-2 w-2 text-water" />
            </div>
            <span>Programada</span>
          </div>
        </div>

        {/* Next care info */}
        <div className="mt-4 pt-4 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            Próxima rega:{' '}
            <span className="font-medium text-foreground">
              {format(new Date(schedule.next_care_date), "d 'de' MMMM", { locale: ptBR })}
            </span>
          </p>
          
          {isEditing ? (
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-xs text-muted-foreground">A cada</span>
              <Input
                type="number"
                min={1}
                max={90}
                value={editFrequency}
                onChange={(e) => setEditFrequency(e.target.value)}
                className="w-16 h-8 text-center text-sm"
              />
              <span className="text-xs text-muted-foreground">dias</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(false)}
                className="h-8 w-8 p-0"
                disabled={saving}
              >
                <X className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                onClick={handleSaveFrequency}
                className="h-8"
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1 mt-1">
              <p className="text-xs text-muted-foreground">
                Frequência: a cada {schedule.frequency_days} dias
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleStartEdit}
                className="h-6 w-6 p-0"
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          )}

          {/* Last watering date edit */}
          {isEditingDate ? (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-muted-foreground">Última rega:</p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9 text-sm"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(editDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar
                    mode="single"
                    selected={editDate}
                    onSelect={(date) => date && setEditDate(date)}
                    disabled={(date) => date > new Date()}
                    initialFocus
                    className="pointer-events-auto"
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
              <div className="flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditingDate(false)}
                  className="h-8"
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveLastWateringDate}
                  className="h-8"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1 mt-2">
              <p className="text-xs text-muted-foreground">
                Última rega: {format(getLastWateringDate(), "d 'de' MMMM", { locale: ptBR })}
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleStartEditDate}
                className="h-6 w-6 p-0"
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}